import { watch } from "vue";

import { boardLoaded } from "@/backend/read/BackendReader";
import type { InfoLevel } from "@/model/baseTypes";
import { replaceQuery } from "@/router/navigation";
import type { SearchQuery } from "@/router/types";
import { isSignalsEnabled } from "@/utils/env/feature";
import { fromString, join, setEquals, toSet } from "@/utils/list";

import { useBoardStore } from "../board";
import { useUrlStore } from "../url";
import type { SearchState, UpperLevelLinksSelection, UrlSyncedSearchState } from "./searchMenu";
import { dependencyLinks, setSearchProps, useSearchMenuStore } from "./searchMenu";
import type { SignalId } from "./signalDefinitions";
import { signalIds } from "./signalDefinitions";

export function initSearchUrlSync() {
  watch(() => useUrlStore().search, readUrl);
  watch(() => useSearchMenuStore().$state, writeUrl, { deep: true });
}

/**
 * Applies a search query from the URL to the search menu store.
 * @internal exported for tests
 */
export async function readUrl(value: SearchQuery) {
  const board = useBoardStore().board;
  if (board) {
    await boardLoaded(board);
  }

  const store = useSearchMenuStore();
  if (Object.values(value).every((v) => v === undefined)) {
    store.resetCriteria();
    return;
  }

  const types = fromString(useBoardStore().possibleStickyTypes, value.type, "name");
  const signalsOn = isSignalsEnabled();
  const urlSearchState = {
    text: value.text || "",
    selectedFlagStrings: toSet(value.flag),
    selectedAssigneeIds: toSet(value.assignee),
    selectedReporterIds: toSet(value.reporter),
    selectedTeamIds: new Set(fromString(store.allTeams, value.team, "name").map((t) => t.id)),
    selectedArtIds: new Set(fromString(store.allArts, value.art, "name").map((a) => a.id)),
    selectedIterationIds: new Set(
      fromString(store.allIterations, value.iteration, "name").map((i) => i.id),
    ),
    selectedStickyTypeIds: new Set(types.map((t) => t.id)),
    selectedStatusIds: toSet(value.status),
    selectedLinkStates: signalsOn ? new Set<InfoLevel>() : (toSet(value.link) as Set<InfoLevel>),
    // with signals, the legacy `link` param (any value) selects risky links
    selectedSignals: new Set<SignalId>(
      signalsOn
        ? [...fromString(signalIds, value.signal), ...(value.link ? (["riskyLinks"] as const) : [])]
        : [],
    ),
    selectedDependencyLink: new Set(
      types.some((t) => t.functionality === "dependency")
        ? fromString(dependencyLinks, value.depLink)
        : [],
    ),
    selectedUpperLevelLinks: deserializeUpperLevelLinks(value.upperLevelLinks),
  };

  if (!sameAsStore(store, urlSearchState)) {
    Object.assign(store, urlSearchState);
    store.expandSearch();
  }
}

function sameAsStore(store: SearchState, p: UrlSyncedSearchState): boolean {
  return (
    store.text === p.text &&
    setSearchProps.every((k) => setEquals(store[k], p[k])) &&
    upperLevelLinksEquals(store.selectedUpperLevelLinks, p.selectedUpperLevelLinks)
  );
}

function upperLevelLinksEquals(a: UpperLevelLinksSelection, b: UpperLevelLinksSelection): boolean {
  return (
    a.type === b.type && (a.type !== "sticky" || b.type !== "sticky" || a.stickyId === b.stickyId)
  );
}

/**
 * Writes the current search menu store state to the URL.
 * @internal exported for tests
 */
export function writeUrl() {
  const store = useSearchMenuStore();
  void replaceQuery({
    search: {
      text: !store.isSearchOpen && !store.text ? undefined : store.text,
      flag: Array.from(store.selectedFlagStrings).join(","),
      assignee: join(store.selectedAssignees, "id"),
      reporter: join(store.selectedReporters, "id"),
      team: join(store.selectedTeams, "name"),
      art: join(store.selectedArts, "name"),
      iteration: join(store.selectedIterations, "name"),
      type: join(store.selectedStickyTypes, "name"),
      depLink: join(store.selectedDependencyLinks),
      status: join(store.selectedStatusItems, "id"),
      ...(isSignalsEnabled()
        ? // "" (not undefined) so replaceQuery removes an old `link` param
          { signal: join(Array.from(store.selectedSignals)), link: "" }
        : { link: join(store.selectedRiskyLinks) }),
      upperLevelLinks: serializeUpperLevelLinks(store.selectedUpperLevelLinks),
    },
  });
}

function deserializeUpperLevelLinks(value: string | undefined): UpperLevelLinksSelection {
  switch (value) {
    case "linked":
      return { type: "linked" };
    case "unlinked":
      return { type: "unlinked" };
    default:
      return value ? { type: "sticky", stickyId: value } : { type: "none" };
  }
}

function serializeUpperLevelLinks(selection: UpperLevelLinksSelection): string {
  switch (selection.type) {
    case "linked":
      return "linked";
    case "unlinked":
      return "unlinked";
    case "sticky":
      return selection.stickyId;
    default:
      return "";
  }
}
